/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Typography } from 'antd';
import RunDebugDetails from '../../../../components/RunDebugDetails';
import { Worker } from '../../../../generated/graphql';
import { useKogEditorContext } from '../Editor/EditorProvider';
import { EditorSourceType } from '../Editor/editorInterface';
import { WorkerState } from './interface';
import styles from './WorkerStatusInfo.module.less';

interface IWorkerStatusInfoProps {
  worker: Worker;
  state: string | null; // can be worker's state or forkExecution's state: mini playground
  currentDocumentToken?: string;
}

const mood: any = {
  Brain: '😐',
  'Execution started': '😐',
  'Preparing Worker': '🤓',
  'Worker ready': '🤓',
  Preparing: '🤓',
  Processing: '🤔',
  Paused: '😎',
  Done: '🤩',
  Failed: '😭'
};

const WorkerStatusInfo: React.FC<IWorkerStatusInfoProps> = (props) => {
  const { worker, state, currentDocumentToken } = props;

  const { source, debugMode, actions, editorStatus } = useKogEditorContext();

  const isMiniPlayground = source === EditorSourceType.MINI_PLAYGROUND;

  const [status, setStatus] = useState<string | null>(null);
  const [statusText, setStatusText] = useState<string>('Processing');

  useEffect(() => {
    switch (status) {
      case WorkerState.CREATED:
      case WorkerState.CREATING:
        setStatusText('Brain');
        break;
      case WorkerState.PREPARING_KNOWLEDGE:
        setStatusText('Preparing Worker');
        break;
      case WorkerState.KNOWLEDGE_READY:
        setStatusText('Worker ready');
        break;
      case WorkerState.DONE:
        setStatusText('Done');
        break;
      case WorkerState.PROCESSING:
        setStatusText('Processing');
        break;
      case WorkerState.ERROR:
        setStatusText('Failed');
        break;
      case WorkerState.WAITING_FOR_USER_INPUT:
        setStatusText('Paused');
        break;
      case WorkerState.STARTED:
      default:
        setStatusText('Execution started');
    }
  }, [status]);

  useEffect(() => {
    if (state) {
      setStatus(state);
    }
  }, [state]);

  useEffect(() => {
    if (editorStatus.isExecuting) {
      setStatus(WorkerState.STARTED);
    }
  }, [editorStatus.isExecuting]);

  return (
    <>
      {/* TODO: Remove once KOG-6748 is resolved. */}
      <div
        data-cy={classNames({
          'new-worker-ready-to-use': [
            WorkerState.CREATED,
            WorkerState.KNOWLEDGE_READY
          ].includes(status as WorkerState)
        })}
      />
      <RunDebugDetails
        worker={worker}
        showCreatedAt
        currentDocumentToken={currentDocumentToken}
        isDebugMode={debugMode}
        handleDebugMode={actions.toggleDebugMode}
      >
        <div style={{ fontSize: isMiniPlayground ? 24 : 32, lineHeight: 1 }}>
          {mood[statusText]}
        </div>
      </RunDebugDetails>
      <Typography.Text
        data-cy="worker-status-text"
        className={styles.statusText}
      >
        {statusText}
      </Typography.Text>
      {statusText === 'Processing' || statusText === 'Preparing Worker' ? (
        <div className="loading-container">
          <div className={styles.loader} data-cy="worker-status-loader" />
        </div>
      ) : null}
    </>
  );
};

export default WorkerStatusInfo;
